import { SchemaGetter } from "effect";
import { Schema } from "effect";

/**
 * Doc Cirrus communication types a patient may review and propose changes to.
 *
 * Every other type the vendor may hold (fax, KIM, social, URL, external, emergency, and any type
 * this list does not name) stays hidden from the patient and is preserved untouched by every write.
 * The list is closed on purpose: an unknown type must never become a writable ordinary contact.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { EligibleContactType } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
 *
 * const type = Schema.decodeUnknownSync(EligibleContactType)("EMAILPRIV");
 * ```
 */
export const EligibleContactType = Schema.Literals(
  ["MOBILEPRIV",
  "PHONEPRIV",
  "MOBILEJOB",
  "PHONEJOB",
  "EMAILPRIV",
  "EMAILJOB"],
);

/**
 * The decoded type of an {@link EligibleContactType}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { EligibleContactType } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
 *
 * const type: EligibleContactType = "PHONEPRIV";
 * ```
 */
export type EligibleContactType = typeof EligibleContactType.Type;

/**
 * Whether an {@link EligibleContactType} carries a telephone number or an e-mail address.
 *
 * Determines which assertion format applies to a proposed value.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { eligibleContactKind } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
 *
 * eligibleContactKind("EMAILJOB"); // "email"
 * eligibleContactKind("MOBILEPRIV"); // "phone"
 * ```
 */
export function eligibleContactKind(
  type: EligibleContactType,
): "phone" | "email" {
  return type === "EMAILPRIV" || type === "EMAILJOB" ? "email" : "phone";
}

const PHONE_MAX_DISPLAY_LENGTH = 40;
const PHONE_MIN_DIGITS = 7;
const PHONE_MAX_DIGITS = 15;
const PHONE_ALLOWED_CHARACTERS = /^[0-9+()/. -]+$/;

const EMAIL_MAX_LENGTH = 254;
const EMAIL_LOCAL_MAX_LENGTH = 64;
const EMAIL_DOMAIN_LABEL = /^[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?$/;

/**
 * Count the digits in a telephone assertion once separators are removed.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { phoneDigitCount } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
 *
 * phoneDigitCount("+49 (0)30 1234-567"); // 12
 * ```
 */
export function phoneDigitCount(value: string): number {
  let digits = 0;
  for (const character of value) {
    if (character >= "0" && character <= "9") digits += 1;
  }
  return digits;
}

/**
 * Reduce a telephone assertion to its comparison form: an optional leading `+` followed by digits.
 *
 * Used to decide whether a proposal already matches what Doc Cirrus holds. The patient's trimmed
 * display value — not this canonical form — is what gets written on a successful save, so formatting
 * the practice already uses is never rewritten by a comparison.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { canonicalPhone } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
 *
 * canonicalPhone("+49 (0)30 1234-567"); // "+490301234567"
 * canonicalPhone("030 1234567") === canonicalPhone("030/1234567"); // true
 * ```
 */
export function canonicalPhone(value: string): string {
  const trimmed = value.trim();
  const hasPlus = trimmed.startsWith("+");
  let digits = "";
  for (const character of trimmed) {
    if (character >= "0" && character <= "9") digits += character;
  }
  return hasPlus ? `+${digits}` : digits;
}

/**
 * Reduce an e-mail assertion to its comparison form: the trimmed value, case-folded.
 *
 * The patient's trimmed submitted value is what gets written; this form only decides equality.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { canonicalEmail } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
 *
 * canonicalEmail(" Anna.Mueller@Example.DE "); // "anna.mueller@example.de"
 * ```
 */
export function canonicalEmail(value: string): string {
  return value.trim().toLowerCase();
}

function isValidPhoneAssertion(value: string): boolean {
  if (value.length === 0 || value.length > PHONE_MAX_DISPLAY_LENGTH)
    return false;
  if (!PHONE_ALLOWED_CHARACTERS.test(value)) return false;
  const plusIndex = value.indexOf("+");
  if (plusIndex > 0) return false;
  if (value.lastIndexOf("+") !== plusIndex) return false;
  const digits = phoneDigitCount(value);
  return digits >= PHONE_MIN_DIGITS && digits <= PHONE_MAX_DIGITS;
}

function isValidEmailAssertion(value: string): boolean {
  if (value.length === 0 || value.length > EMAIL_MAX_LENGTH) return false;
  if (/[\s<>]/.test(value)) return false;
  // eslint-disable-next-line no-control-regex -- control characters are exactly what we reject
  if (/[\u0000-\u001F\u007F]/.test(value)) return false;
  const atIndex = value.indexOf("@");
  if (atIndex <= 0) return false;
  if (value.lastIndexOf("@") !== atIndex) return false;

  const local = value.slice(0, atIndex);
  if (local.length === 0 || local.length > EMAIL_LOCAL_MAX_LENGTH) return false;

  const domain = value.slice(atIndex + 1);
  if (domain.length === 0) return false;
  const labels = domain.split(".");
  if (labels.length < 2) return false;
  if (!labels.every((label) => EMAIL_DOMAIN_LABEL.test(label))) return false;
  const tld = labels[labels.length - 1];
  return tld !== undefined && tld.length >= 2 && /^[A-Za-z]+$/.test(tld);
}

/**
 * A patient-proposed telephone number.
 *
 * Trimmed, at most 40 display characters, digits plus the separators `+ ( ) / . -` and spaces, `+`
 * only as the first character, and 7-15 digits once separators are removed. Letters and extension
 * suffixes are rejected.
 *
 * This is **format validation only**. It does not assert that the patient owns the number: the eGK
 * proves the patient-flow relationship, not control of a telephone line, so a value accepted here
 * must never be written as confirmed.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { PhoneAssertion } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
 *
 * Schema.decodeUnknownSync(PhoneAssertion)("  +49 30 1234567 "); // "+49 30 1234567"
 * ```
 */
export const PhoneAssertion = Schema.Trim.pipe(
  Schema.check(Schema.makeFilter(isValidPhoneAssertion, {
    identifier: "PhoneAssertion",
    description:
      "Telephone number: up to 40 characters, digits with + ( ) / . - and spaces, + only leading, 7-15 digits",
  })),
  Schema.brand("PhoneAssertion"),
);

/**
 * The decoded type of a {@link PhoneAssertion}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { PhoneAssertion } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
 *
 * function display(value: PhoneAssertion): string {
 *   return value;
 * }
 * ```
 */
export type PhoneAssertion = typeof PhoneAssertion.Type;

/**
 * A patient-proposed e-mail address.
 *
 * Trimmed, at most 254 characters, exactly one `@`, a 1-64 character local part with no whitespace,
 * and a dot-separated DNS-style domain with an alphabetic TLD. Display-name wrappers (`<…>`),
 * whitespace, and control characters are rejected.
 *
 * This is **format validation only** and never asserts mailbox ownership — a value accepted here must
 * never be written as confirmed or portal-linked.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { EmailAssertion } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
 *
 * Schema.decodeUnknownSync(EmailAssertion)(" anna.mueller@example.de "); // "anna.mueller@example.de"
 * ```
 */
/**
 * A validated, trimmed e-mail value before ownership semantics are applied.
 *
 * Use this schema for purpose-specific contact fields that validate an address
 * without claiming it is a confirmed patient master-data assertion.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { EmailAddressValue } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
 *
 * Schema.decodeUnknownSync(EmailAddressValue)(" patient@example.de ");
 * ```
 */
export const EmailAddressValue = Schema.Trim.pipe(
  Schema.check(Schema.makeFilter(isValidEmailAssertion, {
    identifier: "EmailAssertion",
    description:
      "E-mail address: up to 254 characters, one @, 1-64 character local part, dot-separated DNS domain",
  })),
);

export const EmailAssertion = EmailAddressValue.pipe(
  Schema.brand("EmailAssertion"),
);

/**
 * The decoded type of an {@link EmailAssertion}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { EmailAssertion } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
 *
 * function display(value: EmailAssertion): string {
 *   return value;
 * }
 * ```
 */
export type EmailAssertion = typeof EmailAssertion.Type;

const HAUSARZT_MAX_LENGTH = 120;

/**
 * German academic/professional title tokens stripped before Hausarzt name comparison.
 *
 * Deliberately explicit and closed rather than pattern-based: a title list that guesses would risk
 * eating a real name component. Compared case-insensitively after whitespace normalization. Shared
 * so that the client's token bound and the server's exact matcher strip precisely the same set.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { HAUSARZT_TITLE_TOKENS } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
 *
 * HAUSARZT_TITLE_TOKENS.includes("dr."); // true
 * ```
 */
export const HAUSARZT_TITLE_TOKENS: readonly string[] = [
  "dr.",
  "dr",
  "prof.",
  "prof",
  "med.",
  "med",
  "dent.",
  "dent",
  "univ.",
  "univ",
  "habil.",
  "habil",
  "h.c.",
  "hc",
  "mag.",
  "mag",
  "dipl.",
  "dipl",
];

/**
 * Normalize Hausarzt free text for comparison: Unicode NFC, trimmed, and all runs of Unicode
 * whitespace collapsed to a single space.
 *
 * Case is deliberately preserved here — case folding belongs to the matcher, while this function is
 * also used to produce the value shown back to the patient and carried into a reception task.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { normalizeHausarztWhitespace } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
 *
 * normalizeHausarztWhitespace("  Dr.  med.   Anna Müller "); // "Dr. med. Anna Müller"
 * ```
 */
export function normalizeHausarztWhitespace(value: string): string {
  return value.normalize("NFC").replace(/\s+/gu, " ").trim();
}

/**
 * Split normalized Hausarzt text into the name tokens that remain once leading title tokens are
 * removed.
 *
 * Only *leading* titles are stripped, so a surname that coincides with a title token cannot be eaten
 * from the middle of a name.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { hausarztNameTokens } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
 *
 * hausarztNameTokens("Dr. med. Anna Müller"); // ["Anna", "Müller"]
 * hausarztNameTokens("Müller"); // ["Müller"]
 * ```
 */
export function hausarztNameTokens(value: string): readonly string[] {
  const tokens = normalizeHausarztWhitespace(value)
    .split(" ")
    .filter((token) => token.length > 0);
  let index = 0;
  while (
    index < tokens.length &&
    HAUSARZT_TITLE_TOKENS.includes((tokens[index] ?? "").toLowerCase())
  ) {
    index += 1;
  }
  return tokens.slice(index);
}

function isValidHausarztAssertion(value: string): boolean {
  return value.length > 0 && value.length <= HAUSARZT_MAX_LENGTH;
}

/**
 * Patient-entered free text identifying their Hausarzt or practice.
 *
 * Whitespace-normalized and capped at 120 characters. It deliberately does not require a personal
 * name: a practice name such as "Gemeinschaftspraxis Am Markt" is a valid patient assertion. The
 * Bridge never writes this text into Doc Cirrus directly; a changed value becomes a reception task.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { HausarztAssertion } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
 *
 * Schema.decodeUnknownSync(HausarztAssertion)("Dr. med.  Anna   Müller"); // "Dr. med. Anna Müller"
 * ```
 */
export const HausarztAssertion = Schema.String.pipe(Schema.decodeTo(Schema.String, {

    decode: SchemaGetter.transform(normalizeHausarztWhitespace),
    encode: SchemaGetter.transform((value) => value),
  })).pipe(
  Schema.check(Schema.makeFilter(isValidHausarztAssertion, {
    identifier: "HausarztAssertion",
    description: "Hausarzt free text: non-empty and at most 120 characters",
  })),
  Schema.brand("HausarztAssertion"),
);

/**
 * The decoded type of a {@link HausarztAssertion}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { HausarztAssertion } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
 *
 * function display(value: HausarztAssertion): string {
 *   return value;
 * }
 * ```
 */
export type HausarztAssertion = typeof HausarztAssertion.Type;

/**
 * A Hausarzt form value, where an empty string means the patient wants the existing entry removed.
 *
 * Non-empty input is decoded by {@link HausarztAssertion}; empty input is converted to `null` by the
 * submit builder and routed to reception like every other Hausarzt change.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { HausarztInput } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
 *
 * Schema.decodeUnknownSync(HausarztInput)("");
 * Schema.decodeUnknownSync(HausarztInput)("Gemeinschaftspraxis Am Markt");
 * ```
 */
export const HausarztInput = Schema.Union(
  [Schema.Literal(""),
  HausarztAssertion],
);

/**
 * The decoded type of {@link HausarztInput}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { HausarztInput } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
 *
 * const value: HausarztInput = "";
 * ```
 */
export type HausarztInput = typeof HausarztInput.Type;

/**
 * Canonical Hausarzt text used only for equality comparison.
 *
 * Display casing is preserved in the assertion and reception task; comparison ignores case and
 * whitespace-only differences so an unchanged value does not create unnecessary work.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { canonicalHausarzt } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
 *
 * canonicalHausarzt(" Dr.  Anna Müller "); // "dr. anna müller"
 * ```
 */
export function canonicalHausarzt(value: string): string {
  return normalizeHausarztWhitespace(value).toLocaleLowerCase("de-DE");
}
